// packages/engine/src/decoration-validate.ts
//
// Validation for decorations and the lifecycle they drive.
//
// Four checks, in the order a user meets them:
//   1. the decoration is declared at all;
//   2. it is allowed on the kind of thing it decorates (this is the check that
//      lets `@retry` sit on a call step while `@planned` is refused for one);
//   3. its arguments match the declared schema;
//   4. a field does not share a name with a decoration, which would make the
//      field unreachable through a getter.
//
// Plus the lifecycle-specific checks: an empty window, a child declared to
// outlive its container, an inverted window, and a date-shaped marker that is
// not a valid date.

import { makeDiag } from "./diagnostics/catalog.js";
import type { Diagnostic } from "./types.js";
import type { Body, Name, QualifiedName, SourceSpan } from "./types.js";
import { lookupKind } from "./resolver.js";
import type {
  ResolvedModel,
  ResolvedModule,
  ResolvedSurface,
  ResolvedTypeRegistry,
} from "./resolver.js";
import {
  DECORATION_TARGET_KINDS,
  EMPTY_DECORATION_REGISTRY,
  argText,
  argValue,
  DERIVED_ONLY_LIFECYCLE_STATES,
  isEmptyWindow,
  isLifecycleDecoration,
  markersFromDecorations,
  primaryFieldOf,
  windowFromDecorations,
  type DecorationDecl,
  type DecorationInstance,
  type DecorationRegistry,
  type LifecycleWindow,
} from "./decorations.js";
import {
  EMPTY_TIMELINE,
  looksLikeDate,
  ordinalOf,
  parseDateLiteral,
  type Timeline,
} from "./timeline.js";

/** What kind of site a decoration was written on — matched against `targets`.
 *  Derived from the kind list rather than mirrored by hand, so a target an
 *  author may name and a site the walk can yield cannot drift apart. */
export type DecorationSite = (typeof DECORATION_TARGET_KINDS)[number];

export function validateDecorations(model: ResolvedModel, diagnostics: Diagnostic[]): void {
  const registry = model.decorationRegistry ?? EMPTY_DECORATION_REGISTRY;
  const timeline = model.timeline ?? EMPTY_TIMELINE;

  // Markers of different families order deterministically but arbitrarily
  // against each other — a date is an instant, a version is a roadmap position.
  // Mixing them is legal and sometimes deliberate (dated history, versioned
  // future), so this is an advisory rather than a refusal.
  if (timeline.families.length > 1) {
    diagnostics.push(
      makeDiag("info", undefined, "LIFECYCLE_MIXED_MARKERS", "validation.lifecycleMixedMarkers", {
        families: timeline.families.join(" + "),
      }),
    );
  }

  // `targets` is reserved — a schema field may not claim it.
  for (const d of model.decorationDecls ?? []) {
    for (const f of d.fields) {
      if (f.name === "targets") {
        diagnostics.push(
          makeDiag(
            "error",
            f.nameSource ?? f.source ?? d.source,
            "DECORATION_RESERVED_FIELD",
            "validation.decorationReservedField",
            { name: d.name },
          ),
        );
      }
    }
  }

  forEachDecorationSite(model, (site) => {
    check(site, registry, timeline, model.registry, diagnostics);
  });

  // Body-scoped and ancestor-scoped checks, which need more than the site.
  walk(model.modules, (m, ancestors) => {
    checkHomonyms(m.body, m.name, registry, diagnostics);
    for (const s of m.surfaces) walkSurfaces(s, (sf) => checkHomonyms(sf.body, sf.name, registry, diagnostics));
    // A child cannot be present when its container is not.
    checkContainment(m, ancestors, registry, timeline, diagnostics);
  });
  for (const t of model.types) checkHomonyms(t.body, t.name, registry, diagnostics);
}

/** One site a decoration was written on, as the walk yields it. */
export interface DecorationSiteRef {
  readonly instances: readonly DecorationInstance[];
  readonly site: DecorationSite;
  readonly owner: Name;
  /** The element's declared type, where it has one — what a `targets: service`
   *  narrowing is matched against. Absent for sites that carry no type
   *  (a field, an aspect, a step). */
  readonly typeName?: Name;
  readonly source?: SourceSpan;
}

/**
 * Every site in the model that can carry a decoration.
 *
 * ONE walk, shared by validation, marker collection and the lifecycle gate.
 * They diverged once — the collector stopped at a module's own surfaces while
 * the node universe recursed into nested surfaces and their interfaces, so a
 * marker written down there never reached the axis, its window resolved against
 * an axis that had never seen it, and the element silently read `live` with no
 * diagnostic anywhere. Sharing the walk is what makes that class impossible.
 */
export function forEachDecorationSite(
  model: Pick<ResolvedModel, "modules" | "processes" | "subprocesses" | "types">,
  visit: (ref: DecorationSiteRef) => void,
): void {
  const body = (b: Body): void => {
    for (const f of b.fields) {
      visit({ instances: f.decorations ?? [], site: "field", owner: f.path.join("."), source: f.source });
    }
    for (const a of b.aspects) {
      visit({ instances: a.decorations ?? [], site: "aspect", owner: a.path.join("."), source: a.source });
    }
  };
  walk(model.modules, (m) => {
    visit({ instances: m.decl.decorations ?? [], site: "module", owner: m.name, typeName: m.typeName, source: m.decl.source });
    body(m.body);
    for (const i of m.interfaces) {
      visit({ instances: i.decl.decorations ?? [], site: "interface", owner: i.name, typeName: i.typeName, source: i.decl.source });
    }
    for (const s of m.surfaces) {
      walkSurfaces(s, (sf) => {
        visit({ instances: sf.decl.decorations ?? [], site: "surface", owner: sf.name, typeName: sf.typeName, source: sf.decl.source });
        body(sf.body);
        for (const i of sf.interfaces) {
          visit({ instances: i.decl.decorations ?? [], site: "interface", owner: i.name, typeName: i.typeName, source: i.decl.source });
        }
      });
    }
  });
  for (const p of model.processes) {
    visit({ instances: p.decorations ?? [], site: "process", owner: p.name, source: p.source });
    for (const step of p.steps) {
      if (step.kind !== "simple") continue;
      visit({ instances: step.decorations ?? [], site: "step", owner: p.name, source: step.source });
    }
  }
  for (const s of model.subprocesses) {
    visit({ instances: s.decorations ?? [], site: "subprocess", owner: s.name, source: s.source });
  }
  for (const t of model.types) {
    visit({ instances: t.decorations ?? [], site: "type", owner: t.name, source: t.source });
    body(t.body);
  }
}

/** A surface and every surface nested inside it. */
function walkSurfaces(s: ResolvedSurface, visit: (s: ResolvedSurface) => void): void {
  visit(s);
  for (const sub of s.surfaces) walkSurfaces(sub, visit);
}

/** Homonym: a field whose name IS a decoration is unreachable via `@name`,
 *  because the getter resolves the declared vocabulary first. Reported on the
 *  FIELD — that is the side that can rename cheaply. */
function checkHomonyms(
  body: Body,
  owner: Name,
  registry: DecorationRegistry,
  diagnostics: Diagnostic[],
): void {
  for (const f of body.fields) {
    const head = f.path[0];
    if (head && registry.kinds.has(head)) {
      diagnostics.push(
        makeDiag("warning", f.pathSource ?? f.source, "DECORATION_HOMONYM", "validation.decorationHomonym", {
          name: head,
          owner,
        }),
      );
    }
  }
}

function check(
  ref: DecorationSiteRef,
  registry: DecorationRegistry,
  timeline: Timeline,
  types: ResolvedTypeRegistry | undefined,
  diagnostics: Diagnostic[],
): void {
  const { instances, site, owner, source: ownerSource } = ref;
  if (!instances || instances.length === 0) return;
  // One window per element per lifecycle kind. A repeat merges field-by-field
  // with last-writer-wins in `windowFromDecorations`, so the earlier bounds
  // disappear silently — report instead of picking. Plain-metadata decorations
  // carry no merge semantics, so repeating one is ordinary authoring.
  const seenLifecycle = new Set<Name>();
  for (const inst of instances) {
    const decl = registry.kinds.get(inst.name);
    if (!decl) {
      // `@retired` / `@live` name a lifecycle STATE, which is derived from the
      // `@planned` / `@deprecated` markers and never written directly. The
      // generic advice ("declare it with `decoration retired { … }`") is worse
      // than nothing here: following it yields a metadata decoration that
      // compiles, paints nothing, and looks like it worked.
      const isState = (DERIVED_ONLY_LIFECYCLE_STATES as readonly string[]).includes(inst.name);
      diagnostics.push(
        makeDiag(
          "error",
          inst.nameSource ?? inst.source,
          "DECORATION_UNKNOWN",
          isState ? "validation.decorationIsLifecycleState" : "validation.decorationUnknown",
          { name: inst.name },
        ),
      );
      continue;
    }
    if (!targetAllows(decl, site, ref.typeName, types, undefined)) {
      diagnostics.push(
        makeDiag("error", inst.nameSource ?? inst.source, "DECORATION_BAD_TARGET", "validation.decorationBadTarget", {
          name: inst.name,
          kind: site,
          targets: (decl.targets ?? DECORATION_TARGET_KINDS).join(", "),
        }),
      );
    }
    checkArgs(inst, decl, diagnostics);
    if (isLifecycleDecoration(inst.name)) {
      if (seenLifecycle.has(inst.name)) {
        diagnostics.push(
          makeDiag("error", inst.nameSource ?? inst.source, "LIFECYCLE_DUPLICATE", "validation.lifecycleDuplicate", {
            name: inst.name,
            owner,
          }),
        );
      }
      seenLifecycle.add(inst.name);
      checkLifecycleArgs(inst, decl, timeline, diagnostics);
    }
  }

  const w = windowFromDecorations(instances, registry, timeline);
  if (isEmptyWindow(w)) {
    diagnostics.push(
      makeDiag("error", ownerSource, "LIFECYCLE_WINDOW_EMPTY", "validation.lifecycleWindowEmpty", {
        name: owner,
      }),
    );
  }
}

/** Names in `targets` that are element KINDS rather than user type names. */
function kindTargets(decl: DecorationDecl): readonly Name[] {
  const kinds = DECORATION_TARGET_KINDS as readonly string[];
  return (decl.targets ?? []).filter((t) => kinds.includes(t));
}

/** Names in `targets` that are user TYPE names — `targets: service`. */
function typeTargets(decl: DecorationDecl): readonly Name[] {
  const kinds = DECORATION_TARGET_KINDS as readonly string[];
  return (decl.targets ?? []).filter((t) => !kinds.includes(t));
}

/**
 * An omitted `targets` means "any element". A kind name matches the site
 * directly; a user type name narrows to instances OF that type, its subtypes
 * included — so `targets: service` accepts a `service` and anything declared
 * `type service payments`.
 *
 * The narrowing used to be waved through: any site whose kind was `module`
 * passed as long as `targets` mentioned one non-kind name, on the reasoning
 * that the real check happened where the type was known. It didn't happen
 * anywhere, so `targets: service` accepted a `database` — the declaration read
 * as a restriction and enforced nothing.
 */
function targetAllows(
  decl: DecorationDecl,
  site: DecorationSite,
  typeName: Name | undefined,
  registry: ResolvedTypeRegistry | undefined,
  space: QualifiedName | undefined,
): boolean {
  if (!decl.targets || decl.targets.length === 0) return true;
  if (kindTargets(decl).includes(site)) return true;
  const wanted = typeTargets(decl);
  if (wanted.length === 0 || typeName === undefined) return false;
  // `chain` is the type's ancestry, so a subtype satisfies an ancestor's name.
  const kind = registry ? lookupKind(registry, typeName, space) : undefined;
  return [typeName, ...(kind?.chain ?? [])].some((n) => wanted.includes(n));
}

function checkArgs(inst: DecorationInstance, decl: DecorationDecl, diagnostics: Diagnostic[]): void {
  const known = new Set(decl.fields.map((f) => f.name));
  const primary = primaryFieldOf(decl);
  const seen = new Set<string>();
  for (const a of inst.args) {
    const name = a.name ?? primary;
    if (a.name !== undefined && !known.has(a.name) && a.name !== "description") {
      diagnostics.push(
        makeDiag("error", a.nameSource ?? a.source, "DECORATION_UNKNOWN_ARG", "validation.decorationUnknownArg", {
          name: inst.name,
          arg: a.name,
        }),
      );
      continue;
    }
    // Only NAMED args can genuinely set the same field twice. Two positionals
    // both bind to the primary and so look like a duplicate here, but that is
    // the arity/mixing error the parser already reports — saying it twice, in
    // two different vocabularies, just makes the real message harder to find.
    if (seen.has(name) && a.name !== undefined) {
      diagnostics.push(
        makeDiag("error", a.nameSource ?? a.source, "DECORATION_DUPLICATE_ARG", "validation.decorationDuplicateArg", {
          name: inst.name,
          arg: name,
        }),
      );
    }
    seen.add(name);
  }
}

/**
 * Lifecycle args need no registry to resolve — every marker written in the model
 * IS on the timeline by construction. What is left to check is that the two ends
 * of a deprecation window are the right way round, and that a marker which
 * clearly meant to be a date actually is one.
 */
function checkLifecycleArgs(
  inst: DecorationInstance,
  decl: DecorationDecl,
  timeline: Timeline,
  diagnostics: Diagnostic[],
): void {
  const toText = argText(argValue(inst, decl, "to"));
  const fromText = argText(argValue(inst, decl, "from"));

  // `from` opens the transition, `to` closes it. Backwards, the window does not
  // merely look odd — it ERASES the transition zone, because `lifecycleStateAt`
  // tests `to` first and so never reports `deprecated`.
  const from = fromText === undefined ? undefined : ordinalOf(timeline, fromText);
  const to = toText === undefined ? undefined : ordinalOf(timeline, toText);
  if (from !== undefined && to !== undefined && from > to) {
    diagnostics.push(
      makeDiag("error", inst.source, "LIFECYCLE_WINDOW_INVERTED", "validation.lifecycleWindowInverted", {
        name: inst.name,
      }),
    );
  }

  // A date-SHAPED marker that is not a valid date (`2026-13-99`) still lands on
  // the axis — as free text, sorted nowhere near the dates it meant to sit
  // among. That is always a typo, so it is reported rather than tolerated.
  for (const text of [toText, fromText]) {
    if (text === undefined || parseDateLiteral(text) !== undefined) continue;
    if (!looksLikeDate(text)) continue;
    diagnostics.push(
      makeDiag("error", inst.source, "LIFECYCLE_MALFORMED_DATE", "validation.lifecycleMalformedDate", {
        text,
      }),
    );
  }
}

function checkContainment(
  m: ResolvedModule,
  ancestors: readonly ResolvedModule[],
  registry: DecorationRegistry,
  timeline: Timeline,
  diagnostics: Diagnostic[],
): void {
  const own = windowFromDecorations(m.decl.decorations ?? [], registry, timeline);
  if (!hasBounds(own)) return;
  for (const anc of ancestors) {
    const up = windowFromDecorations(anc.decl.decorations ?? [], registry, timeline);
    if (!hasBounds(up)) continue;
    const bornEarlier =
      own.bornFrom !== undefined && up.bornFrom !== undefined && own.bornFrom < up.bornFrom;
    const diesLater = own.deadTo !== undefined && up.deadTo !== undefined && own.deadTo > up.deadTo;
    if (bornEarlier || diesLater) {
      diagnostics.push(
        makeDiag(
          "error",
          m.decl.source,
          "LIFECYCLE_OUTLIVES_CONTAINER",
          "validation.lifecycleOutlivesContainer",
          { name: m.name, container: anc.name },
        ),
      );
      return;
    }
  }
}

function hasBounds(w: LifecycleWindow): boolean {
  return w.bornFrom !== undefined || w.deadFrom !== undefined || w.deadTo !== undefined;
}

/**
 * Whether ANY site in the model carries a lifecycle decoration.
 *
 * The gate in front of the reference walk, which needs a graph and a universe.
 * Rides {@link forEachDecorationSite} so it cannot see fewer sites than the
 * validator does.
 */
export function modelCarriesLifecycle(
  model: Pick<ResolvedModel, "modules" | "processes" | "subprocesses" | "types">,
): boolean {
  let hit = false;
  forEachDecorationSite(model, (site) => {
    if (!hit) hit = site.instances.some((d) => isLifecycleDecoration(d.name));
  });
  return hit;
}

/**
 * Every timeline marker the model mentions, in no particular order — the raw
 * material `buildTimeline` sorts into the axis.
 *
 * A decoration written where its `targets` forbid it contributes nothing: it is
 * already an error, and minting a tick for it would put a marker on the axis
 * that no legal decoration ever names.
 */
export function collectModelMarkers(
  model: Pick<ResolvedModel, "modules" | "processes" | "subprocesses" | "types">,
  registry: DecorationRegistry,
  types?: ResolvedTypeRegistry,
): string[] {
  const out: string[] = [];
  forEachDecorationSite(model, (site) => {
    const legal = site.instances.filter((inst) => {
      const decl = registry.kinds.get(inst.name);
      return decl !== undefined && targetAllows(decl, site.site, site.typeName, types, undefined);
    });
    if (legal.length > 0) markersFromDecorations(legal, registry, out);
  });
  return out;
}

function walk(
  modules: readonly ResolvedModule[],
  visit: (m: ResolvedModule, ancestors: readonly ResolvedModule[]) => void,
  ancestors: readonly ResolvedModule[] = [],
): void {
  for (const m of modules) {
    visit(m, ancestors);
    walk(m.modules, visit, [...ancestors, m]);
  }
}
